import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BOOKING_URL = "https://link.sweatsetter.com/widget/booking/0bvqp14e0y6pNvAdcqsM";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_awesome-napier-3/artifacts/gnp9xnt5_top2.jpg";

const navLinks = [
  { name: 'Programs', path: '/programs' },
  { name: 'HYROX', path: '/hyrox' },
  { name: 'Schedule', path: '/schedule' },
  { name: 'Join', path: '/join' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
  { name: 'Members', path: 'https://xteamfitness.com/members', external: true },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <header 
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
        isScrolled ? 'shadow-md' : ''
      }`}
      style={{ height: '135px' }}
    >
      <div className="h-full flex items-center" style={{ maxWidth: '1720px', margin: '0 auto', padding: '0 2rem' }}>
        <div className="flex items-center justify-between w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="X-Team Fitness" 
              style={{ width: '385px', height: 'auto' }}
              loading="eager"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav" aria-label="Main navigation">
            {navLinks.map((link) => (
              link.external ? (
                <a
                  key={link.path}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  className="font-nav text-xl uppercase tracking-[0.15em] text-gray-800 hover:text-[#FF5A3C] transition-colors duration-200"
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  className={`font-nav text-xl uppercase tracking-[0.15em] transition-colors duration-200 ${
                    location.pathname === link.path
                      ? 'text-[#FF5A3C]'
                      : 'text-gray-800 hover:text-[#FF5A3C]'
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                data-testid="header-cta-btn"
                className="bg-[#FF5A3C] hover:bg-[#E64526] text-white rounded-none font-nav text-lg uppercase tracking-[0.15em] px-8 py-6 shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
              >
                Book A Free Intro
                <ArrowRight className="w-5 h-5" />
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2"
            data-testid="mobile-menu-btn"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu-panel"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-gray-700" />
            ) : (
              <Menu className="w-6 h-6 text-gray-700" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div 
          id="mobile-menu-panel"
          className="lg:hidden bg-white border-t border-gray-100 shadow-lg"
          data-testid="mobile-menu"
          role="dialog"
          aria-label="Mobile navigation menu"
        >
          <nav className="py-4" style={{ maxWidth: '1720px', margin: '0 auto', padding: '1rem 2rem' }} aria-label="Mobile navigation">
            <Link
              to="/"
              data-testid="mobile-nav-link-home"
              className={`block py-3 font-nav text-base uppercase tracking-[0.15em] transition-colors duration-200 ${
                location.pathname === '/'
                  ? 'text-[#FF5A3C]'
                  : 'text-gray-800'
              }`}
            >
              Home
            </Link>
            {navLinks.map((link) => (
              link.external ? (
                <a
                  key={link.path}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  className="block py-3 font-nav text-base uppercase tracking-[0.15em] text-gray-800"
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  className={`block py-3 font-nav text-base uppercase tracking-[0.15em] transition-colors duration-200 ${
                    location.pathname === link.path
                      ? 'text-[#FF5A3C]'
                      : 'text-gray-800'
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
